import { ActionButton, Modal, MultiSegmentControl } from "@indigo-components";
import Button from "@legacy-components/general/buttons/Button";
import { generateClient } from "aws-amplify/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppointmentRecord,
  AppointmentType,
  FileType,
  ReportSource,
} from "~/api/API";
import { updateAppointmentRecordWithConfig } from "~/api/graphql-custom/mutations";
import { useUploadTabLinkedFiles } from "~/api/queries/useUploadTabLinkedFiles";
import { getAssessmentSource } from "~/common/converters";
import {
  isSupportedReportGenerationAppointmentType,
  SupportedReportGenerationAppointmentType,
} from "~/common/types";
import { useModalBlocking } from "~/hooks/useModalBlocking";

const client = generateClient({ authMode: "userPool" });

/*
Should be similar to ReportUISelectedSource (src/pydantic_models/lambdas/models/workflow.ts)
Consider have some kind of type guard to ensure user-code type is ReportUISelectedSource compatible
*/
export enum InputMethod {
  DICTATION = "DICTATION",
  WRITE = "WRITE",
}

export enum ModalVariant {
  EVALUATION = "evaluation",
  RE_EVALUATION = "re-evaluation",
  SUPPLEMENTAL = "supplemental",
  GENERAL = "general",
}

// History and physical exam are always written by the physician, so the
// assessment is the only section whose input method is configurable.
export interface SectionOptions {
  assessment: InputMethod;
}

interface ReportGenerationOptionsModalProps {
  isOpen: boolean;
  onConfirm: (options: SectionOptions) => Promise<void>;
  onCancel: () => void;
  appointment: AppointmentRecord;
  doctorName: string;
}

const inputOptions = [
  { label: "Dictate", value: InputMethod.DICTATION },
  { label: "Write", value: InputMethod.WRITE },
];

const MODAL_TITLE: Record<ModalVariant, string> = {
  [ModalVariant.EVALUATION]: "Evaluation report for Dr. %s",
  [ModalVariant.RE_EVALUATION]: "Re-evaluation report for Dr. %s",
  [ModalVariant.SUPPLEMENTAL]: "Supplemental report for Dr. %s",
  [ModalVariant.GENERAL]: "Report for Dr. %s",
};

const getModalVariant = (appointmentType: AppointmentType): ModalVariant => {
  switch (appointmentType) {
    case AppointmentType.QME_REEVALUATION:
    case AppointmentType.AME_REEVALUATION:
    case AppointmentType.SIBTF_REEVALUATION:
    case AppointmentType.IME_REEVALUATION:
    case AppointmentType.IME_ADR_REEVALUATION:
      return ModalVariant.RE_EVALUATION;
    case AppointmentType.SUPPLEMENTAL:
      return ModalVariant.SUPPLEMENTAL;
    case AppointmentType.QME:
    case AppointmentType.AME:
    case AppointmentType.SIBTF:
    case AppointmentType.IME:
    case AppointmentType.IME_ADR:
      return ModalVariant.EVALUATION;
    case AppointmentType.BRIEFING:
    case AppointmentType.DEPOSITION:
    case AppointmentType.RECORD_REVIEW:
      return ModalVariant.GENERAL;
  }
};

const ReportGenerationOptionsModal = ({
  isOpen,
  onConfirm,
  onCancel,
  appointment,
  doctorName,
}: ReportGenerationOptionsModalProps) => {
  const { modalProps, getBlockableOnCancel, startBlocking, stopBlocking } =
    useModalBlocking();
  const [assessment, setAssessment] = useState<InputMethod>(InputMethod.WRITE);

  const { files } = useUploadTabLinkedFiles(appointment);
  const hasDictationFiles = useMemo(
    () =>
      files.some(
        (file) => file.fileType === FileType.DICTATION && !file.isReadOnly,
      ),
    [files],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    // Initialize based on priority:
    // 1. Existing appointment configuration (if available)
    // 2. Doctor preferences (via getAssessmentSource)
    // 3. Hard-coded default (WRITE)
    let initialAssessment = InputMethod.WRITE;
    if (appointment.reportConfiguration) {
      initialAssessment =
        appointment.reportConfiguration.assessment === ReportSource.WRITE
          ? InputMethod.WRITE
          : InputMethod.DICTATION;
    } else if (
      appointment.type &&
      isSupportedReportGenerationAppointmentType(appointment.type)
    ) {
      initialAssessment =
        getAssessmentSource(
          appointment,
          appointment.type as SupportedReportGenerationAppointmentType,
          undefined,
        ) === "WRITE"
          ? InputMethod.WRITE
          : InputMethod.DICTATION;
    }

    // If no dictation files available, force WRITE
    if (!hasDictationFiles) {
      initialAssessment = InputMethod.WRITE;
    }

    setAssessment(initialAssessment);
    // Only re-run when the specific fields we use change, not the entire appointment object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointment.type,
    appointment.reportConfiguration,
    appointment.doctor?.evaluationInputPreferences,
    hasDictationFiles,
  ]);

  const handleConfirm = useCallback(async () => {
    startBlocking("confirm");
    const options: SectionOptions = { assessment };
    try {
      // Save configuration. History and physical exam are always written.
      const reportConfiguration = {
        history: ReportSource.WRITE,
        physicalExamination: ReportSource.WRITE,
        assessment:
          assessment === InputMethod.WRITE
            ? ReportSource.WRITE
            : ReportSource.DICTATE,
      };

      await client.graphql({
        query: updateAppointmentRecordWithConfig,
        variables: {
          input: {
            id: appointment.id,
            reportConfiguration,
          },
        },
      });

      await onConfirm(options);
    } catch (error) {
      console.error("Failed to save report configuration:", error);
      // Still proceed with report generation even if saving config fails
      await onConfirm(options);
    } finally {
      stopBlocking("confirm");
    }
  }, [appointment.id, assessment, onConfirm, startBlocking, stopBlocking]);

  const isDepositionType = appointment.type === AppointmentType.DEPOSITION;

  if (isDepositionType) {
    console.warn("Deposition is not allowed to generate reports");
    return null;
  }

  const modalVariant = appointment.type
    ? getModalVariant(appointment.type)
    : ModalVariant.EVALUATION;
  const toggleOptions = inputOptions.map((option) => ({
    ...option,
    disabled: option.value === InputMethod.DICTATION && !hasDictationFiles,
  }));

  const onAssessmentChange = (value: string) =>
    setAssessment(value as InputMethod);

  return (
    <Modal
      title={MODAL_TITLE[modalVariant].replace("%s", doctorName)}
      open={isOpen}
      onCancel={getBlockableOnCancel(onCancel)}
      footer={null}
      centered
      width={450}
      closable={modalProps.closable}
      maskClosable={modalProps.maskClosable}
    >
      <div className="flex flex-col gap-6 py-4">
        <div className="flex items-center justify-between w-full">
          <span className="font-inter text-[14px] font-semibold leading-[16.94px] text-indigo-text-primary">
            Assessment
          </span>
          <MultiSegmentControl
            label=""
            value={assessment}
            options={toggleOptions}
            onChange={onAssessmentChange}
          />
        </div>

        <div className="flex justify-end gap-[10px] mt-4">
          <Button
            sizeVariant="small"
            variant="borderless"
            colorVariant="gray"
            onClick={onCancel}
            disabled={!modalProps.closable}
          >
            Cancel
          </Button>
          <ActionButton
            variant="primary"
            size="md"
            onClick={handleConfirm}
            text="Confirm"
          />
        </div>
      </div>
    </Modal>
  );
};

export default ReportGenerationOptionsModal;
